import type { ReactNode } from 'react';

import styles from './Tabs.module.css';

export interface TabItem {
  disabled?: boolean;
  id: string;
  /** 允许 ReactNode：页签可携带徽标（如「奖励设置 必填」）。 */
  label: ReactNode;
}

export interface TabsProps {
  ariaLabel: string;
  className?: string;
  /** 用于派生标签与面板 id：`<idPrefix>-<id>-tab` / `<idPrefix>-<id>-panel`。 */
  idPrefix: string;
  onChange: (id: string) => void;
  tabs: readonly TabItem[];
  value: string;
  /** 默认下划线页签；segmented 为 Figma 的紧凑分段式页签。 */
  variant?: 'underline' | 'segmented';
}

export function Tabs({ ariaLabel, className, idPrefix, onChange, tabs, value, variant = 'underline' }: TabsProps) {
  function handleKeyDown(event: React.KeyboardEvent<HTMLDivElement>) {
    const step = event.key === 'ArrowRight' ? 1 : event.key === 'ArrowLeft' ? -1 : 0;
    const edgeKey = variant === 'segmented' && (event.key === 'Home' || event.key === 'End');
    if (step === 0 && !edgeKey) return;
    const enabled = tabs.filter((tab) => !tab.disabled);
    const index = enabled.findIndex((tab) => tab.id === value);
    if (index === -1) return;
    event.preventDefault();
    const next = enabled[event.key === 'Home' ? 0 : event.key === 'End' ? enabled.length - 1 : (index + step + enabled.length) % enabled.length];
    onChange(next.id);
    if (variant === 'segmented') document.getElementById(`${idPrefix}-${next.id}-tab`)?.focus();
  }

  return (
    <div aria-label={ariaLabel} className={[styles.root, className].filter(Boolean).join(' ')} data-variant={variant} onKeyDown={handleKeyDown} role="tablist">
      {tabs.map((tab) => <button
        aria-controls={`${idPrefix}-${tab.id}-panel`}
        aria-selected={tab.id === value}
        className={styles.item}
        data-disabled={tab.disabled || undefined}
        disabled={tab.disabled}
        id={`${idPrefix}-${tab.id}-tab`}
        key={tab.id}
        onClick={() => onChange(tab.id)}
        role="tab"
        tabIndex={tab.id === value ? 0 : -1}
        type="button"
      >{tab.label}</button>)}
    </div>
  );
}
